import { giphyBlack, giphyLightCharcoal, giphyLightestGrey } from '@giphy/js-brand'
import React, { useEffect, useState } from 'react'
import { fetchRelatedKeywords } from 'shared/api'
import { STATUS } from 'shared/redux/creation-tool'
import { googleAnalyticsEvent } from 'shared/util/analytics'
import styled from 'styled-components'
import Placeholder from './placeholder'
import SuggestedTagsList from './suggested-tags-list'
import WhatsThis from './whats-this'

const Container = styled.div`
    width: 100%;
    background: ${giphyBlack};
    border: 1px dashed ${giphyLightCharcoal};
    border-radius: 4px;
    font-size: 14px;
    margin-bottom: 15px;
`

const TopRow = styled.div`
    width: 100%;
    height: 25%;
    position: relative;
    margin-bottom: 5px;
`

const Headline = styled.span`
    font-size: 15px;
    font-weight: bold;
    display: inline-block;
    margin: 10px;
    color: ${giphyLightestGrey};
`

const BetaPill = styled.img`
    position: relative;
    display: inline-block;
    margin-bottom: -5px;
    height: 20px;
`

const encodeGAParams = p =>
    Object.entries(p)
        .map(kv => kv.map(encodeURIComponent).join('='))
        .join('&')

type Props = {
    tags: string[]
    endpoint: string
    lastEnteredTags: string[]
    gifID: string
    addSugTag: (tag: string) => void
    status?: string
    isVertical: boolean
}

const SuggestedTagWrapper = ({ tags, endpoint, lastEnteredTags, gifID, addSugTag, status, isVertical }: Props) => {
    const [suggestedTagResults, setSuggestedTagResults] = useState<object>({})
    const [isFetching, setIsFetching] = useState<boolean>(false)

    useEffect(() => {
        if (lastEnteredTags.length > 0) {
            fetchRelatedTags(lastEnteredTags)
        }
        if (status === STATUS.UPLOADING) {
            logUnusedSuggestedTags()
        }
    }, [lastEnteredTags, status])

    const addSuggestedTag = (tag: string) => {
        addSugTag(tag)
        Object.entries(suggestedTagResults).forEach(result => {
            const sugTags = result[1].suggestedTags
            const tagIndex = sugTags.indexOf(tag)
            if (tagIndex > -1) {
                // In case of edit panel, need to handle comma-separated gifID list
                gifID.split(',').map(gif => {
                    googleAnalyticsEvent({
                        category: endpoint,
                        action: 'Tag Accepted',
                        label: encodeGAParams({
                            gif_id: gif,
                            tag_name: tag,
                            tag_source: 'related',
                            response_id: result[1]['response_id'],
                        }),
                    })
                })
            }
        })
        removeSuggestedTag(tag, true)
    }

    const removeSuggestedTag = (tag: string, wasAdded = false) => {
        const newSuggestedTagResults = { ...suggestedTagResults }
        Object.values(newSuggestedTagResults).forEach((result: any) => {
            const sugTags = result.suggestedTags
            const tagIndex = sugTags.indexOf(tag)
            if (tagIndex > -1) {
                const newTags = [...sugTags]
                newTags.splice(tagIndex, 1)
                result.suggestedTags = newTags
                if (!wasAdded) {
                    // In case of edit panel, need to handle comma-separated gifID list
                    gifID.split(',').map(gif => {
                        googleAnalyticsEvent({
                            category: endpoint,
                            action: 'Tag Rejected',
                            label: encodeGAParams({
                                gif_id: gif,
                                tag_name: tag,
                                tag_source: 'related',
                                response_id: result.response_id,
                            }),
                        })
                    })
                }
            }
        })
        setSuggestedTagResults(newSuggestedTagResults)
    }

    const logUnusedSuggestedTags = () => {
        // TODO: Implement Seen events in a not-stupid way, make sure it gets called when closing overlay and edit panels.
    }

    const fetchRelatedTag = async (tag: string) => {
        try {
            const {
                data = [],
                meta: { response_id = '' },
            } = await fetchRelatedKeywords(tag)

            return {
                response_id: response_id,
                suggestedTags: data.map(tag => tag.name),
            }
        } catch (error) {
            console.log(error)
            return {}
        }
    }

    const fetchRelatedTags = async (newTags: string[]) => {
        setIsFetching(true)
        logUnusedSuggestedTags()
        let results = {}
        const promises: Promise<any>[] = []
        newTags.forEach(tag => {
            if (tag.length > 0) {
                promises.push(
                    fetchRelatedTag(tag).then(data => {
                        results[tag] = { ...data }
                    })
                )
            }
        })
        await Promise.all(promises)
        setIsFetching(false)
        setSuggestedTagResults(results)
    }

    const aggregateSuggestedTags = (results: object) => {
        // Zipper merge the results of multiple related tag queries into one list. Filter out existing tags.
        // Thanks SO https://stackoverflow.com/a/47061337
        return Object.entries(results)
            .map(function([_, resultObject]) {
                // only return suggested tags if resultObject exists otherwise return empty array
                if (resultObject && resultObject.suggestedTags) {
                    return resultObject.suggestedTags
                } else {
                    return []
                }
            })
            .reduce(function(groupedTags, nextTags) {
                // zip together each tag's suggestedTags
                nextTags.forEach((tag, i) => (groupedTags[i] = groupedTags[i] || []).push(tag))
                return groupedTags
            }, [])
            .reduce(function(flattenedTags, nextTags) {
                // flatten the grouped tags by concatenating each list with the next
                return flattenedTags.concat(nextTags)
            }, [])
            .filter(function(tag) {
                return !Object.keys(suggestedTagResults).includes(tag)
            })
    }

    return (
        <Container>
            <TopRow>
                <Headline>Suggested Tags</Headline>
                {!isVertical && <BetaPill src="/static/img/svg/pill-beta-gradient.svg" alt="" />}
                <WhatsThis isVertical={isVertical} />
            </TopRow>
            {tags.length < 1 || isFetching ? (
                <Placeholder isFetching={isFetching} />
            ) : (
                <SuggestedTagsList
                    suggestedTags={aggregateSuggestedTags(suggestedTagResults)}
                    onAdd={addSuggestedTag}
                    onDelete={removeSuggestedTag}
                    isVertical={isVertical}
                    lastEnteredTags={lastEnteredTags}
                />
            )}
        </Container>
    )
}

export default SuggestedTagWrapper
